import React from "react";
import CustomImage from "@/app/components/common/Image";
import { inter, philosopher } from "@/lib/fonts";
import { routes } from "@/lib/constants";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ICategory } from "muminsmart-utils";

interface CategoriesProps {
  categories: ICategory[];
}

const Categories: React.FC<CategoriesProps> = ({ categories }) => {
  const navigate = useNavigate();
  const routeParams = useParams<{ categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const rightPanelRef = React.useRef<HTMLDivElement | null>(null);

  const activeCategoryId =
    routeParams.categoryId ?? searchParams.get("categoryId");

  const selectedCategory = React.useMemo(() => {
    if (!categories.length) {
      return null;
    }

    if (activeCategoryId) {
      return (
        categories.find((category) => category.id === activeCategoryId) ??
        categories[0]
      );
    }

    return categories[0];
  }, [categories, activeCategoryId]);

  React.useEffect(() => {
    rightPanelRef.current?.scrollTo({ top: 0 });
  }, [activeCategoryId]);

  const onCategoryClick = (category: ICategory) => {
    if (category.id === activeCategoryId) {
      return;
    }

    navigate(routes.categories.id(category.id), {
      replace: true,
      preventScrollReset: true,
    });
  };

  return (
    <div className="flex">
      {/* Left Panel */}
      <div className="flex flex-col gap-3 shrink-0 basis-1/4 md:basis-1/6 lg:basis-1/10 h-[calc(100vh-126px)] md:h-[calc(100vh-126px)] lg:md:h-[calc(100vh-126px)] overflow-y-auto shadow-[15px_0_20px_-20px_rgba(184,127,44,0.5)] scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {categories.map((item) => (
          <div
            key={item.id}
            className={`flex flex-col text-center items-center px-3 ${item.id === selectedCategory?.id ? "border-l-3 border-[#F2994A]" : ""} cursor-pointer transition`}
            onClick={() => onCategoryClick(item)}
          >
            <CustomImage
              src={item.image}
              alt={item.name}
              className="w-full rounded-lg bg-linear-to-t from-20% from-[#F0E0C7] to-70% to-transparent"
            />
            <div>
              <h3
                className={`text-center text-lg text-[#6F6F6F] py-1 ${philosopher.className} text-wrap`}
              >
                {item.name}
              </h3>
            </div>
          </div>
        ))}
      </div>
      {/* Right Panel */}
      <div
        ref={rightPanelRef}
        className="flex-1 h-[calc(100vh-126px)] md:h-[calc(100vh-126px)] lg:md:h-[calc(100vh-126px)] overflow-y-auto space-y-2"
      >
        {selectedCategory && selectedCategory.subCategories?.length ? (
          selectedCategory.subCategories.map((subCat) => (
            <div key={subCat.id} className="px-4">
              <h1 className={`text-md font-medium ${inter.className}`}>
                {subCat.name}
              </h1>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 p-4 sm:gap-3 md:gap-4">
                {subCat?.subSubCategories?.map((item) => (
                  <Link
                    to={routes.categories.subCategories.subSubCategories.id(
                      item.categoryId,
                      item.subCategoryId,
                      item.id,
                    )}
                    key={item.id}
                    className="w-full cursor-pointer transition"
                  >
                    <div
                      className={`flex flex-col text-center items-center ${item.id === selectedCategory?.id ? "border-l-6 border-[#F2994A]" : ""} cursor-pointer transition`}
                    >
                      <CustomImage
                        src={item.image}
                        alt={item.name}
                        className="w-full rounded-lg bg-linear-to-t from-20% from-[#F0E0C7] to-70% to-transparent"
                      />
                      <div>
                        <h3
                          className={`text-center text-sm leading-4 py-1 ${inter.className} text-wrap`}
                        >
                          {item.name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="px-4 border-b border-[#F2994A]" />
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full">
            <h2 className="text-gray-500">Select a category to view details</h2>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;
